import { Box } from "@mui/material";
import Highcharts from "highcharts";
import PriceChart from "../PriceChart";
import SalesChart from "../SalesChart";
import OffersChart from "../OffersChart";
import { memo, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import { createTooltipPositioner, debounce } from "../../helpers";
import { useAppSelector } from "../../store/hooks";
import { selectProductAnalyzerSettings } from "../../features/settings/settings.selector";
import { selectChartsData } from "../../features/productAnalyzer/productAnalyzer.selector";

type TProductChartsProps = {
  rangeFilter: number;
  interactionMode: "zooming" | "panning";
};

type TChartRef = {
  chart?: Highcharts.Chart | null;
};

const isMouseEvent = (event: MouseEvent | TouchEvent): event is MouseEvent =>
  "clientX" in event;

export const ProductCharts = ({
  interactionMode,
  rangeFilter,
}: TProductChartsProps) => {
  const { showPricesChart, showSoldChart, showOffersChart } = useAppSelector(
    selectProductAnalyzerSettings,
  );
  const { chartsData } = useAppSelector(selectChartsData);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRefs = useRef<(TChartRef | null)[]>([]);
  const lastRangeFilterRef = useRef(rangeFilter);
  const isSyncingExtremesRef = useRef(false);

  const charts = useMemo(() => {
    const res = [];
    if (showPricesChart) res.push(PriceChart);
    if (showSoldChart) res.push(SalesChart);
    if (showOffersChart) res.push(OffersChart);
    return res;
  }, [
    showPricesChart,
    showSoldChart,
    showOffersChart,
    PriceChart,
    SalesChart,
    OffersChart,
  ]);

  // Calculate earliest data date, ignoring data points older than 365 days
  const earliestDataDate = useMemo(() => {
    if (!chartsData || chartsData.length === 0) return undefined;

    const minAllowedDate = moment().subtract(365, "days").valueOf();
    let earliest: number | undefined = undefined;

    // Iterate through all chart data arrays
    chartsData.forEach((dataArray) => {
      if (dataArray && dataArray.length > 0) {
        // Find the earliest date in this array, ignoring dates before (now - 365 days)
        dataArray.forEach((dataPoint) => {
          if (dataPoint && dataPoint.date) {
            const dateTimestamp = moment(dataPoint.date).valueOf();
            // Only consider dates that are within the last 365 days
            if (dateTimestamp >= minAllowedDate) {
              if (earliest === undefined || dateTimestamp < earliest) {
                earliest = dateTimestamp;
              }
            }
          }
        });
      }
    });

    return earliest;
  }, [chartsData]);

  // Calculate minimum date for x-axis based on range filter
  // Use the earliest data date if it's more recent than the calculated date
  const minDateTimestamp = useMemo(() => {
    if (rangeFilter === 0) return undefined; // "All time filter"

    const calculatedMinDate = moment().subtract(rangeFilter, "days").valueOf();

    // If we have data and the earliest data date is more recent than calculated date,
    // use the earliest data date to avoid showing empty space
    if (earliestDataDate && earliestDataDate > calculatedMinDate) {
      return earliestDataDate;
    }

    return calculatedMinDate;
  }, [rangeFilter, earliestDataDate]);

  // Update charts when interaction mode or date range changes
  useEffect(() => {
    chartRefs.current.forEach((ref) => {
      ref?.chart?.update({
        xAxis: { min: minDateTimestamp },
        chart: {
          panning: { enabled: interactionMode === "panning" },
          zooming: { type: interactionMode === "zooming" ? "x" : undefined },
        },
      });
    });
  }, [minDateTimestamp, interactionMode]);

  // Reset the charts when the minDateTimestamp changes to their initial state
  useEffect(() => {
    if (lastRangeFilterRef.current !== rangeFilter) {
      lastRangeFilterRef.current = rangeFilter;
      chartRefs.current.forEach((ref) => {
        ref?.chart?.xAxis[0]?.setExtremes(undefined, undefined);
      });
    }
  }, [rangeFilter]);

  // Setup synchronized tooltips and crosshairs across all charts
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Track tooltip x position
    let xPos = 0;

    // Configure tooltip positioner for all charts
    chartRefs.current.forEach((ref) => {
      ref?.chart?.update({
        tooltip: {
          animation: false,
          positioner: createTooltipPositioner(() => xPos),
        },
      });
    });

    // Show tooltips and crosshairs on mouse/touch move
    const revealInfo = (e: MouseEvent | TouchEvent) => {
      const rect = container.getBoundingClientRect();
      const clientX = !isMouseEvent(e) ? e.touches[0].clientX : e.clientX;

      xPos = clientX - rect.left;

      let targetInsidePlot = false;

      // Check if pointer is inside any chart's plot area
      chartRefs.current.forEach((ref) => {
        const chart = ref?.chart;
        if (!chart || !chart.container.contains(e.target as Node)) return;

        const pointerEvent = chart.pointer.normalize(e);
        targetInsidePlot = chart.isInsidePlot(
          pointerEvent.chartX - chart?.plotLeft,
          pointerEvent.chartY - chart?.plotTop,
          { visiblePlotOnly: true },
        );
      });

      if (!targetInsidePlot) {
        hideInfo();
        return;
      }

      // Show tooltips and crosshairs for all charts except the one being hovered
      chartRefs.current.forEach((ref) => {
        const chart = ref?.chart;
        if (!chart || chart.container.contains(e.target as Node)) return;

        const pointerEvent = chart.pointer.normalize(e);
        const point = chart.series
          .map((s) => s.searchPoint(pointerEvent, true))
          .find(Boolean);

        if (point) {
          chart.tooltip.refresh(point);
          chart.xAxis[0].drawCrosshair(pointerEvent, point);
        }
      });
    };

    // Hide tooltips and crosshairs
    const hideInfo = () => {
      chartRefs.current.forEach((ref) => {
        const chart = ref?.chart;
        if (!chart) return;

        chart.tooltip.hide(300);
        chart.xAxis[0].hideCrosshair();
      });
    };

    const debouncedRevealInfo = debounce(revealInfo, 0);

    // Attach event listeners for mouse/touch interactions
    container?.addEventListener("mousemove", debouncedRevealInfo, {
      passive: true,
    });
    container?.addEventListener("mouseleave", hideInfo);
    container?.addEventListener("touchmove", debouncedRevealInfo, {
      passive: true,
    });
    container?.addEventListener("touchend", hideInfo);

    // Cleanup event listeners
    return () => {
      container?.removeEventListener("mousemove", debouncedRevealInfo);
      container?.removeEventListener("mouseleave", hideInfo);
      container?.removeEventListener("touchmove", debouncedRevealInfo);
      container?.removeEventListener("touchend", hideInfo);
    };
  }, []);

  // Synchronize panning and zooming (xAxis extremes) across all charts
  useEffect(() => {
    const disposers: Array<Function> = [];

    chartRefs.current.forEach((ref, sourceIndex) => {
      const xAxis = ref?.chart?.xAxis[0];
      if (!xAxis) return;

      const dispose = Highcharts.addEvent(
        xAxis,
        "setExtremes",
        (e: Highcharts.AxisSetExtremesEventObject) => {
          if (isSyncingExtremesRef.current) return;
          isSyncingExtremesRef.current = true;

          chartRefs.current.forEach((ref, targetIndex) => {
            if (targetIndex === sourceIndex) return;
            const targetAxis = ref?.chart?.xAxis[0];
            targetAxis?.setExtremes(e.min, e.max, true, false);
          });

          isSyncingExtremesRef.current = false;
        },
      );
      disposers.push(dispose);
    });

    return () => {
      disposers.forEach((dispose) => {
        dispose();
      });
    };
  }, []);

  return (
    <Box ref={containerRef}>
      {charts.map((Chart, index) => (
        <Chart
          ref={(ref) => (chartRefs.current[index] = ref)}
          fbmMinDateTimestamp={minDateTimestamp}
          key={index}
        />
      ))}
    </Box>
  );
};
